import React from 'react';
import { View, StyleSheet, ViewStyle, StyleProp } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../../context/ThemeContext';

interface SafeAreaScreenProps {
    children: React.ReactNode;
    /** Background color override. Defaults to theme canvas. */
    backgroundColor?: string;
    /** Which edges to inset. Defaults to all. */
    edges?: ('top' | 'bottom' | 'left' | 'right')[];
    /** Optional content style. */
    contentStyle?: StyleProp<ViewStyle>;
    /** Set to true for immersive screens that should bleed into status bar
     *  (LiveRoom, AgapePractice hero). Disables top safe-area inset. */
    immersive?: boolean;
}

/**
 * Wraps a screen with theme-aware safe-area handling. Use on screens that
 * skip the global <Header> (auth screens, immersive views) so they still
 * respect notches / nav-bars on native and match the canvas background.
 *
 * For screens with <Header>, the header itself owns the top safe-area —
 * don't wrap with SafeAreaScreen there or you'll double-pad.
 */
export const SafeAreaScreen: React.FC<SafeAreaScreenProps> = ({
    children,
    backgroundColor,
    edges,
    contentStyle,
    immersive = false,
}) => {
    const { colors } = useTheme();
    const bg = backgroundColor ?? colors.canvas;
    const resolvedEdges = edges ?? (immersive ? ['bottom', 'left', 'right'] : ['top', 'bottom', 'left', 'right']);

    return (
        <SafeAreaView
            edges={resolvedEdges}
            style={[styles.root, { backgroundColor: bg }]}
        >
            <View style={[styles.content, contentStyle]}>
                {children}
            </View>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    root: {
        flex: 1,
    },
    content: {
        flex: 1,
    },
});
